import * as React from 'react';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { Formik, Field, ErrorMessage, Form } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import FileUpload from '../../../components/FileUpload';
import AddLaterRadio from '../../../components/AddLaterRadio';
import Button from '../../../components/Button';
import ExistingCertification from '../../../components/ExistingCertification';

// TYPES
import { FileType } from '../../../components/FileUpload';
import { CertificationTypes } from './CertificationStaff';
import { AccountExistingCertificationsCertification } from '../../../api/models';

interface PartFiveTypes {
	certificationData: CertificationTypes;
	setCertificationFormData: React.Dispatch<React.SetStateAction<CertificationTypes>>;
	setFormSection: React.Dispatch<React.SetStateAction<string>>;
	doJoin: () => void;
	loading: boolean;
	formErrorMessage?: string;
	existingCertifications: AccountExistingCertificationsCertification[];
}

interface vaccinationType {
	vaccinationCertificate?: string;
	createAttachments?: string[];
	attachments?: FileType[];
	addLater?: boolean;
	acknowledge?: boolean;
	userAcknowledgement?: boolean;
	hasExistingCertification?: boolean;
}

const PartFive: React.FC<PartFiveTypes> = ({
	certificationData,
	setCertificationFormData,
	doJoin,
	loading,
	formErrorMessage,
	existingCertifications
}) => {
	// Check if User is logged in or not
	const isUserLoggedIn = useAppSelector((state) => state.user.loggedIn);

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	const [updateCount, setUpdateCount] = React.useState<number>(0);

	// Set Initial Values
	const PartFiveInitialValues: vaccinationType = {
		attachments:
			certificationData && certificationData.vaccinationCertificate
				? certificationData.vaccinationCertificate.attachments
				: [],
		createAttachments:
			certificationData && certificationData.vaccinationCertificate
				? certificationData.vaccinationCertificate.createAttachments
				: [],
		addLater:
			existingCertifications &&
			existingCertifications.length > 0 &&
			existingCertifications.filter((item) => item.validCertification).length > 0
				? true
				: false,
		acknowledge: certificationData && certificationData.vaccinationCertificate ? certificationData.acknowledge : false,
		userAcknowledgement:
			certificationData && certificationData.vaccinationCertificate
				? certificationData.vaccinationCertificate.userAcknowledgement
				: false,
		hasExistingCertification: existingCertifications && existingCertifications.length > 0 ? true : false
	};

	// Submit form
	const continueFormStepFive = (values: any) => {
		setCertificationFormData((prevState) => ({
			...prevState,

			acknowledge: values.acknowledge,
			vaccinationCertificate: {
				userAcknowledgement: values.userAcknowledgement,
				createAttachments: values.createAttachments
			}
		}));

		if (values.addLater) {
			setCertificationFormData((prevState) => ({
				...prevState,

				vaccinationCertificate: {
					createAttachments: [],
					attachments: [],
					userAcknowledgement: values.userAcknowledgement
				},
				acknowledge: values.acknowledge
			}));
		}

		doJoin();
	};

	// Validation
	const formFiveValidationSchema = Yup.object().shape({
		createAttachments: Yup.array().when('addLater', {
			is: false,
			then: Yup.array().min(1, 'Required').required('Required')
		}),
		userAcknowledgement: Yup.bool().when('addLater', {
			is: true,
			then: Yup.bool().when('hasExistingCertification', {
				is: true,
				then: Yup.bool().oneOf([true], 'Required').required('Required')
			}),
			otherwise: Yup.bool().oneOf([true], 'Required').required('Required')
		}),
		acknowledge: Yup.bool().oneOf([true], 'Required').required('Required')
	});

	return (
		<Formik
			validationSchema={formFiveValidationSchema}
			onSubmit={continueFormStepFive}
			initialValues={PartFiveInitialValues}
			// enableReinitialize={true}
		>
			{(formik) => {
				const uploadFile = (val: string[], files: any[]) => {
					if (!formik.values.addLater) {
						if (formik.values.createAttachments) {
							setCertificationFormData((prevState) => ({
								...prevState,
								vaccinationCertificate: {
									...prevState.vaccinationCertificate,
									createAttachments: val,
									attachments: files
								}
							}));
						}
					}
				};

				return (
					<>
						<Form translate="yes">
							<div className="form-group">
								<label htmlFor="files" className="label">
									5. Please provide a vaccination certificate
								</label>

								<p className="form-group">Please provide evidence of your vaccination status</p>

								<Field
									children={() => (
										<>
											<FileUpload
												keyValue={updateCount.toString()}
												name="createAttachments"
												placeholder="Choose file..."
												multiple={true}
												onChange={(val: string[], files: any[]) => {
													formik.setFieldValue('createAttachments', val), uploadFile(val, files);
													formik.setFieldValue('addLater', false);
												}}
											/>
										</>
									)}
								/>

								<ErrorMessage name={`createAttachments`}>
									{(error) => <div className="form-validation-error">{error}</div>}
								</ErrorMessage>
							</div>

							<AddLaterRadio
								changeToUseExisting={existingCertifications && existingCertifications.length > 0}
								onChange={(val: boolean) => {
									formik.setFieldValue('addLater', !formik.values.addLater);
								}}
								value={formik.values.addLater}
							/>

							{existingCertifications && existingCertifications.length > 0 ? (
								<ExistingCertification existingCertifications={existingCertifications} />
							) : null}

							<div className="form-group acknowledge-container mbtom-0">
								<label className="form-control-checkbox">
									<Field
										name="userAcknowledgement"
										type="checkbox"
										onChange={() => {
											formik.setFieldValue('userAcknowledgement', !formik.values.userAcknowledgement);
											setCertificationFormData((prevState) => ({
												...prevState,
												vaccinationCertificate: {
													...prevState.vaccinationCertificate,
													userAcknowledgement: !formik.values.userAcknowledgement
												}
											}));
										}}
									/>

									<span>
										I acknowledge that I am up to date with all vaccinations required by my organisation and by law
										(unless a medical exemption applies).
									</span>
								</label>

								<ErrorMessage name="userAcknowledgement">
									{(error) => <div className="form-validation-error">{error}</div>}
								</ErrorMessage>
							</div>

							<div className="general-divider"></div>

							<div className="form-group acknowledge-container">
								<label className="form-control-checkbox">
									<Field
										name="acknowledge"
										type="checkbox"
										onChange={() => {
											formik.setFieldValue('acknowledge', !formik.values.acknowledge);
											setCertificationFormData((prevState) => ({
												...prevState,
												acknowledge: !formik.values.acknowledge
											}));
										}}
									/>

									<span>
										I acknowledge that all organisations that I choose to join on ChildAware will be able to view (a) my
										profile information (including any documents that I have uploaded) (b) the names of the orgnisations
										where I work or volunteer with children; and (c) my roles at these organisations. The Individual
										User{' '}
										<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
											Terms and Conditions
										</a>{' '}
										and{' '}
										<a href={`${websiteUrl}/privacy-policy`} target="_blank">
											Privacy Policy
										</a>{' '}
										continue to apply.
									</span>
								</label>

								<ErrorMessage name="acknowledge">
									{(error) => <div className="form-validation-error">{error}</div>}
								</ErrorMessage>
							</div>

							{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}

							<Button type="submit" className="btn btn-primary full-width" disabled={loading}>
								{isUserLoggedIn ? 'Request to join' : 'Create Account'}
							</Button>
						</Form>
					</>
				);
			}}
		</Formik>
	);
};

export default PartFive;
